import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "@/components/admin/AdminLayout";
import { usePlaybook, useCreatePlaybook, useUpdatePlaybook } from "@/hooks/usePlaybooks";
import { useIsAdmin } from "@/hooks/useAdmin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { getIcon, iconOptions } from "@/lib/iconMap";
import { ArrowLeft, Save, Loader2, Shield } from "lucide-react";
import { toast } from "sonner";

const categories = ["survival", "operations", "scaling"];
const tiers = ["free", "pro", "scale"];

export default function AdminPlaybookEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === "new";
  
  const { data: isAdmin, isLoading: adminLoading } = useIsAdmin();
  const { data: playbook, isLoading } = usePlaybook(isNew ? undefined : id);
  const createPlaybook = useCreatePlaybook();
  const updatePlaybook = useUpdatePlaybook();

  const [form, setForm] = useState({
    title: "",
    description: "",
    icon_name: "BookOpen",
    category: "operations",
    tier: "free",
    estimated_time: "5 min",
    is_published: false,
    sort_order: 0,
  });

  useEffect(() => {
    if (playbook) {
      setForm({
        title: playbook.title,
        description: playbook.description,
        icon_name: playbook.icon_name,
        category: playbook.category,
        tier: playbook.tier,
        estimated_time: playbook.estimated_time,
        is_published: playbook.is_published,
        sort_order: playbook.sort_order,
      });
    }
  }, [playbook]);

  if (adminLoading || (!isNew && isLoading)) {
    return (
      <AdminLayout>
        <div className="p-8 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AdminLayout>
    );
  }

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="p-8 text-center">
          <Shield className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold text-foreground mb-2">Access Denied</h1>
          <p className="text-muted-foreground">You don't have admin access.</p>
        </div>
      </AdminLayout>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isNew) {
        const created = await createPlaybook.mutateAsync(form);
        toast.success("Playbook created");
        navigate(`/admin/playbooks/${created.id}/nodes`);
      } else {
        await updatePlaybook.mutateAsync({ id: id!, ...form });
        toast.success("Playbook updated");
        navigate("/admin/playbooks");
      }
    } catch (error) {
      toast.error("Failed to save playbook");
    }
  };

  const Icon = getIcon(form.icon_name);

  return (
    <AdminLayout>
      <div className="p-8 max-w-2xl mx-auto">
        <Button variant="ghost" onClick={() => navigate("/admin/playbooks")} className="mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Playbooks
        </Button>

        <Card>
          <CardHeader>
            <CardTitle>{isNew ? "Create New Playbook" : "Edit Playbook"}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="title">Title</Label>
                <Input
                  id="title"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g., Late Return Protocol"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="Brief description of what this playbook covers"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Icon</Label>
                  <Select
                    value={form.icon_name}
                    onValueChange={(value) => setForm({ ...form, icon_name: value })}
                  >
                    <SelectTrigger>
                      <SelectValue>
                        <div className="flex items-center gap-2">
                          <Icon className="h-4 w-4" />
                          {form.icon_name}
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {iconOptions.map((name) => {
                        const IconOption = getIcon(name);
                        return (
                          <SelectItem key={name} value={name}>
                            <div className="flex items-center gap-2">
                              <IconOption className="h-4 w-4" />
                              {name}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={form.category}
                    onValueChange={(value) => setForm({ ...form, category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat} value={cat}>
                          {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Access Tier</Label>
                  <Select
                    value={form.tier}
                    onValueChange={(value) => setForm({ ...form, tier: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {tiers.map((tier) => (
                        <SelectItem key={tier} value={tier}>
                          {tier.charAt(0).toUpperCase() + tier.slice(1)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="time">Estimated Time</Label>
                  <Input
                    id="time"
                    value={form.estimated_time}
                    onChange={(e) => setForm({ ...form, estimated_time: e.target.value })}
                    placeholder="e.g., 5 min"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="sort">Sort Order</Label>
                <Input
                  id="sort"
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })}
                />
              </div>

              <div className="flex gap-4">
                <Button type="submit" disabled={createPlaybook.isPending || updatePlaybook.isPending}>
                  {(createPlaybook.isPending || updatePlaybook.isPending) && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  <Save className="mr-2 h-4 w-4" />
                  {isNew ? "Create Playbook" : "Save Changes"}
                </Button>
                <Button type="button" variant="outline" onClick={() => navigate("/admin/playbooks")}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
